<script setup>
import {ref, onMounted, nextTick, watch, onUnmounted} from "vue";

const props = defineProps({
    title: String,
    name: String,
    required: Boolean,
    data: {
        type: Array,
        default: () => []
    },
    deleteRoute: {
        type: String,
        default: '/private-api/file'
    },
    uploadRoute: {
        type: String,
        default: '/private-api/file/upload'
    },
    fileableType: {
        type: String,
    },
    fileableId: {
        type: Number,
    },
});


const fileInput = ref(null);
const files = ref(props.data || []);
const uploading = ref(false);

// Add these reactive variables at the top of your script
const currentFileableType = ref(props.fileableType);
const currentFileableId = ref(props.fileableId);

const emit = defineEmits(['files-changed']);


// Watch for data changes (when editing different records)
watch(() => props.data, (newData) => {
    files.value = newData || [];
}, {deep: true});

const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
        const result = await uploadFile(file);
        if (result.success) {
            successCount++;
        } else {
            errorCount++;
        }
    }

    // Reset file input
    if (fileInput.value) {
        fileInput.value.value = '';
    }

    // Emit events instead of refreshing
    if (successCount > 0) {
        // Dispatch custom DOM event for Blade template
        window.dispatchEvent(new CustomEvent('file-uploaded', {
            detail: { count: successCount }
        }));
    }
    if (errorCount > 0) {
        showToast('error', `${errorCount} file(s) failed to upload`);
    }
};

const uploadFile = async (file) => {
    uploading.value = true;

    try {
        const formData = new FormData();
        formData.append('file', file);

        if (currentFileableType.value) {
            formData.append('fileable_type', currentFileableType.value);
        }

        if (currentFileableId.value) {
            formData.append('fileable_id', currentFileableId.value);
        }

        const response = await fetch(props.uploadRoute, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
            },
            body: formData
        });
        const result = await response.json();

        if (result.success || (result.code && result.code === 200)) {
            files.value.push(result.data);
            emitFilesChanged();

            // Show success toast using your existing system
            window.showToast(`File "${file.name}" uploaded successfully!`, 'success');
            return { success: true };
        } else {
            // Show error toast using your existing system
            const errorMessage = result.errors?.file?.[0] || 'Upload failed';
            window.showToast(`Failed to upload "${file.name}": ${errorMessage}`, 'error');
            return { success: false, error: errorMessage };
        }

    } catch (error) {
        window.showToast(`Failed to upload "${file.name}": ${error}`, 'error');
        return { success: false, error: 'Network error' };
    } finally {
        uploading.value = false;
    }
};

const removeFile = async (fileId) => {
    const deleteUrl = props.deleteRoute.replace(':id', fileId);
    const reason = prompt('Please enter delete reason:');

    if (reason && confirm('Are you sure to delete this file?')) {
        try {
            await axios.post(deleteUrl, { reason: reason });

            // Remove from local array after successful deletion
            const fileIndex = files.value.findIndex(file => file.id === fileId);
            if (fileIndex !== -1) {
                files.value.splice(fileIndex, 1);
                emitFilesChanged();
                // Dispatch custom DOM event for Blade template
                window.dispatchEvent(new CustomEvent('file-deleted', {
                    detail: { fileId }
                }));
                window.showToast(`File remove successfully!`, 'success');
            }
        } catch (error) {
            window.showToast(`Failed to delete this file: ${error}`, 'error');
        }
    }
};

const getFileUrl = (file) => {
    // Use the src property that's now included in JSON
    return file.src || file.src_url || (file.getTemporaryUrl ? file.getTemporaryUrl() : null);
};

const getFileIcon = (file) => {
    const mimeType = file.mime_type || '';
    if (mimeType.startsWith('image/')) {
        return 'ki-outline ki-image';
    } else if (mimeType.startsWith('video/')) {
        return 'ki-outline ki-video';
    } else if (mimeType.startsWith('audio/')) {
        return 'ki-outline ki-music';
    } else if (mimeType.includes('pdf')) {
        return 'ki-outline ki-file-pdf';
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
        return 'ki-outline ki-file-document';
    } else {
        return 'ki-outline ki-file';
    }
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const emitFilesChanged = () => {
    emit('files-changed', files.value);
};

// Expose methods to parent
defineExpose({
    getFiles: () => files.value,
    clearFiles: () => {
        files.value = [];
        if (fileInput.value) {
            fileInput.value.value = '';
        }
    },
    setFiles: (newFiles) => {
        files.value = newFiles || [];
    }
});

</script>

<template>
    <div class="flex flex-col">
        <label v-if="title" class="form-label font-normal text-gray-900 required flex-row items-center mb-2">
            <span>{{ title }}</span>
            <span v-if="required" class="text-danger">*</span>
        </label>

        <div class="flex flex-col gap-3 mb-4">
            <input
                ref="fileInput"
                type="file"
                :name="name"
                class="file-input"
                multiple
                @change="handleFileSelect"
                :disabled="uploading"
            />
            <div v-if="uploading" class="text-sm text-gray-500">
                <i class="ki-outline ki-loading animate-spin"></i>
                Uploading files...
            </div>
        </div>

        <div v-if="files.length > 0">
            <ul class="space-y-2">
                <li v-for="(file, index) in files" :key="file.id"
                    class="grid grid-flow-col grid-cols-6 items-center gap-2 py-2 border-b border-gray-200">
                    <div class="col-span-5 flex gap-3 items-center">
                        <span class="text-center text-xs text-gray-500">{{ index + 1 }}</span>
                        <i :class="getFileIcon(file)" class="text-lg text-gray-600"></i>
                        <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</div>
                            <div class="text-xs text-gray-500">
                                {{ formatFileSize(file.size) }} • {{ file.extension?.toUpperCase() }}
                            </div>
                        </div>
                    </div>
                    <div class="col-span-1 flex gap-2 justify-end">
                        <a :href="getFileUrl(file)" target="_blank"
                           class="btn btn-outline btn-primary btn-xs">
                            <i class="ki-outline ki-eye"></i>
                        </a>
                        <a href="#"
                           @click.prevent="removeFile(file.id)"
                           class="btn btn-outline btn-danger btn-xs">
                            <i class="ki-outline ki-trash"></i>
                        </a>
                    </div>
                </li>
            </ul>
        </div>

    </div>
</template>
